// Календар за избор на диапазон: месец с навигация ‹ ›, седмици от понеделник,
// първи клик = начало, втори = край (по-ранен ден сменя началото). Денят под
// фокус се мести със стрелките (±1 ден, ±7 дни), Enter/Space го избира.
import { useEffect, useRef, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

type Props = {
  from: Dayjs | null;
  to: Dayjs | null;
  onChange: (from: Dayjs | null, to: Dayjs | null) => void;
  // последният ден, който може да се избере (бъдещето няма документи)
  maxDate?: Dayjs;
};

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const RangeCalendar = ({ from, to, onChange, maxDate }: Props) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState<Dayjs>((to ?? from ?? dayjs()).startOf("month"));
  const [focusDay, setFocusDay] = useState<Dayjs>((to ?? from ?? dayjs()).startOf("day"));
  const gridRef = useRef<HTMLDivElement | null>(null);
  const keyboardMovedRef = useRef(false);

  // Фокусът следва деня само когато е местен от клавиатурата
  useEffect(() => {
    if (!keyboardMovedRef.current) return;
    keyboardMovedRef.current = false;
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-day="${focusDay.format("YYYY-MM-DD")}"]`)
      ?.focus();
  }, [focusDay, month]);

  const firstCell = month.startOf("month").subtract((month.startOf("month").day() + 6) % 7, "day");
  const cells = Array.from({ length: 42 }, (_, i) => firstCell.add(i, "day"));
  // Шестият ред се показва само ако месецът стига до него
  const visibleCells = cells[35].month() === month.month() ? cells : cells.slice(0, 35);

  const pick = (day: Dayjs) => {
    if (maxDate && day.isAfter(maxDate, "day")) return;
    if (!from || (from && to)) onChange(day, null);
    else if (day.isBefore(from, "day")) onChange(day, null);
    else onChange(from, day);
  };

  const moveFocus = (step: number) => {
    const next = focusDay.add(step, "day");
    keyboardMovedRef.current = true;
    setFocusDay(next);
    if (!next.isSame(month, "month")) setMonth(next.startOf("month"));
  };

  const onGridKeyDown = (event: React.KeyboardEvent) => {
    const steps: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const step = steps[event.key];
    if (step == null) return;
    event.preventDefault();
    moveFocus(step);
  };

  const inRange = (day: Dayjs) =>
    !!from &&
    !!to &&
    !day.isBefore(from, "day") &&
    !day.isAfter(to, "day");

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMonth(month.subtract(1, "month"))}
          aria-label={t("inventoryPage.prevMonth")}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-[7px] border border-shade-3 bg-surface text-ink hover:border-shade-5"
        >
          <LeftOutlined className="text-[11px]" />
        </button>
        <span className="flex-1 text-center text-sm font-semibold capitalize text-ink">
          {t(`inventoryPage.month${month.month()}`)} {month.year()}
        </span>
        <button
          type="button"
          onClick={() => setMonth(month.add(1, "month"))}
          aria-label={t("inventoryPage.nextMonth")}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-[7px] border border-shade-3 bg-surface text-ink hover:border-shade-5"
        >
          <RightOutlined className="text-[11px]" />
        </button>
      </div>
      <div
        ref={gridRef}
        role="grid"
        onKeyDown={onGridKeyDown}
        className="grid grid-cols-7 gap-y-0.5"
      >
        {WEEKDAY_KEYS.map((key) => (
          <span
            key={key}
            className="py-1 text-center text-[11px] font-semibold text-muted"
          >
            {t(`inventoryPage.weekday_${key}`)}
          </span>
        ))}
        {visibleCells.map((day) => {
          const outside = !day.isSame(month, "month");
          const disabled = !!maxDate && day.isAfter(maxDate, "day");
          const isEdge =
            (!!from && day.isSame(from, "day")) || (!!to && day.isSame(to, "day"));
          const between = inRange(day) && !isEdge;
          const isToday = day.isSame(dayjs(), "day");
          const isFocus = day.isSame(focusDay, "day");
          return (
            <button
              key={day.format("YYYY-MM-DD")}
              type="button"
              data-day={day.format("YYYY-MM-DD")}
              tabIndex={isFocus ? 0 : -1}
              disabled={disabled}
              onClick={() => {
                setFocusDay(day);
                pick(day);
              }}
              className={`h-9 cursor-pointer border-none font-sheet text-[13px] outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] disabled:cursor-default disabled:opacity-35 ${
                isEdge
                  ? "rounded-md bg-brand font-bold text-white"
                  : between
                    ? "rounded-none bg-brand-tint text-brand-deep-ink"
                    : `rounded-md bg-transparent hover:bg-shade-1 ${
                        outside ? "text-faint" : "text-ink"
                      }`
              } ${isToday && !isEdge ? "shadow-[inset_0_0_0_1px_var(--color-brand)]" : ""}`}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RangeCalendar;
